import React, { useState, useEffect } from 'react'
import {
    Box,
    ContentLayout,
    SpaceBetween,
    Table,
    Header,
    ButtonDropdown,
    Button,
    Toggle,
    FormField,
} from '@amzn/awsui-components-react'
import HeaderTemplate from '../reusable/HeaderTemplate'
import CreateEditBannerModal from './CreateEditBannerModal'
import { HEADER_BG_COLOR, ModalModes } from '../../Constant'
import {
    BannerType,
    bannerVisibleColumns,
    getColumnDef,
    EMPTY_BANNER,
    convertMilliToSecEpochTime,
    shouldShowBanner,
} from './BannerUtil'
import { useAppContext } from '../../../context'
import EmptyState from '../reusable/EmptyState'
import useStore from '../../Store'

const SetupBanner = (props) => {
    const { bannerAlerts, setBannerAlerts, dismissAlerts, refreshAppBanners } = props
    const appContext = useAppContext()
    const apiClient = appContext.apiClient
    const selectBusinessEntity = useStore((state) => state.selectBusinessEntity)
    const businessEntitiesFromStore = useStore((state) => state.businessEntities)
    const [selectedBanners, setSelectedBanners] = useState<any>([])
    const [bannerModalVisible, setBannerModalVisible] = React.useState(false)
    const [mode, setMode] = useState<any>(ModalModes.NOT_SET)
    const [banners, setBanners] = useState<BannerType[]>([EMPTY_BANNER])
    const [visibleBanners, setVisibleBanners] = useState<BannerType[]>([EMPTY_BANNER])
    const [bannersLoading, setBannersLoading] = useState<boolean>(true)
    const [hideExpiredMessages, setHideExpiredMessages] = useState<boolean>(true)

    // Helper function to check if a banner is expired
    const isBannerExpired = (banner: BannerType): boolean => {
        const currentTime = convertMilliToSecEpochTime(Date.now())
        return banner.ttl < currentTime
    }

    // Process banners based on filter and sort settings
    useEffect(() => {
        // Admin table shows all banners regardless of is_falcon/is_egret flags
        let processedBanners = [...banners]

        // Filter out expired messages if toggle is enabled
        if (hideExpiredMessages) {
            processedBanners = processedBanners.filter((banner) => !isBannerExpired(banner))
        }

        // Sort by expiry date (ttl) - latest expiry first
        processedBanners.sort((a, b) => b.ttl - a.ttl)

        setVisibleBanners(processedBanners)
    }, [banners, hideExpiredMessages])

    const getBanners = () => {
        setBannersLoading(true)
        apiClient
            .get(`/banners`)
            .then((response) => {
                setBanners(response.data)
                setBannersLoading(false)
            })
            .catch((error) => {
                console.error(error)
            })
    }

    const removeBannerAlerts = (banner_id) => {
        setBannerAlerts(bannerAlerts.filter((alert) => alert.id !== banner_id))
    }

    useEffect(() => {
        getBanners()
    }, [])

    const handleBannerDelete = () => {
        apiClient
            .delete(`/banner/${selectedBanners[0].value}`)
            .then(() => {
                getBanners()
                removeBannerAlerts(selectedBanners[0].value)
                setSelectedBanners([])
            })
            .catch((error) => {
                console.error(error)
            })
    }

    return (
        <ContentLayout
            defaultPadding
            headerBackgroundStyle={HEADER_BG_COLOR}
            headerVariant='high-contrast'
            header={
                <Box margin={{ left: 's', right: 's' }}>
                    <SpaceBetween size='xs' direction='vertical'>
                        <HeaderTemplate
                            items={[
                                { text: 'Home', href: '/' },
                                { text: 'Banner Portal', href: '' },
                            ]}
                        />
                    </SpaceBetween>
                </Box>
            }
        >
            <CreateEditBannerModal
                mode={mode}
                modalVisible={bannerModalVisible}
                setModalVisible={setBannerModalVisible}
                selectedBanner={selectedBanners[0]}
                setSelectedBanner={setSelectedBanners}
                getBanners={getBanners}
                setBannerAlerts={setBannerAlerts}
                bannerAlerts={bannerAlerts}
                dismissAlerts={dismissAlerts}
                refreshAppBanners={refreshAppBanners}
            />
            <Box padding={{ left: 'm', right: 'm' }} margin={{ top: 's', left: 's', right: 's' }}>
                <SpaceBetween size='m'>
                    <Table
                        items={visibleBanners}
                        selectionType={'single'}
                        columnDefinitions={getColumnDef(businessEntitiesFromStore)}
                        visibleColumns={bannerVisibleColumns}
                        selectedItems={selectedBanners}
                        onSelectionChange={({ detail }) => {
                            setSelectedBanners(detail.selectedItems)
                        }}
                        loading={bannersLoading}
                        empty={
                            <EmptyState
                                title='No Banner Messages'
                                subtitle='No banners to display.'
                                action={
                                    <Button
                                        onClick={() => {
                                            setMode(ModalModes.CREATE)
                                            setBannerModalVisible(true)
                                        }}
                                    >
                                        Create new banner
                                    </Button>
                                }
                            />
                        }
                        header={
                            <Header
                                variant='h2'
                                actions={
                                    <SpaceBetween size='s' direction='horizontal'>
                                        <FormField label={'Hide expired messages'}></FormField>
                                        <Toggle
                                            onChange={({ detail }) =>
                                                setHideExpiredMessages(detail.checked)
                                            }
                                            checked={hideExpiredMessages}
                                        ></Toggle>
                                        <Button
                                            variant='primary'
                                            onClick={() => {
                                                setMode(ModalModes.CREATE)
                                                setSelectedBanners([])
                                                setBannerModalVisible(true)
                                            }}
                                        >
                                            {'Create a Banner'}
                                        </Button>
                                        <ButtonDropdown
                                            items={[
                                                {
                                                    text: 'Edit',
                                                    id: 'edit_banner',
                                                    disabled: selectedBanners.length !== 1,
                                                },
                                                {
                                                    text: 'Delete',
                                                    id: 'delete_banner',
                                                    disabled: selectedBanners.length === 0,
                                                },
                                            ]}
                                            onItemClick={({ detail }) => {
                                                if (detail.id === 'edit_banner') {
                                                    setMode(ModalModes.EDIT)
                                                    setBannerModalVisible(true)
                                                } else {
                                                    setMode(ModalModes.DELETE)
                                                    handleBannerDelete()
                                                }
                                            }}
                                        >
                                            Actions
                                        </ButtonDropdown>
                                    </SpaceBetween>
                                }
                            >
                                {'Existing Banners'}
                            </Header>
                        }
                    />
                </SpaceBetween>
            </Box>
        </ContentLayout>
    )
}

export default SetupBanner
